import { WizardNavigationService } from "@/shared/Wizard/services/WizardNavigationService";
import { AnalysisWizardSharedModel } from "../models/AnaylsisWizardSharedModel";
import { AnalysisWizardStep } from "../models/AnalysisWizardStep";
import { WizardStepAvailabiltyModel } from "@/shared/Wizard/models/WizardStepAvailabilityModel";
import { AnalysisWizardStepType } from "../models/AnyalsisWizardStepType";
import DataProcessingStepUnavailable from "../DataProcessingStep/components/DataProcessingStepUnavailable.vue";
import PostProcessingStepUnavailable from "../PostProcessingStep/components/PostProcessingStepUnavailable.vue";
import { WizardModel } from "@/shared/Wizard/models/WizardModel";
import AnalysisConfigurationStepUnavailable from "../AnalysisConfig/components/AnalysisConfigurationStepUnavailable.vue";
import { WizardStepStatusType } from "@/shared/Wizard/models/WizardStep";
import CustomCalculationStepUnavailable from "../CustomCalculationsStep/components/CustomCalculationStepUnavailable.vue";
import { ExecutionStatusType } from "@/shared/Executions/models/ExecutionStatusType";
import CustomReportStepUnavailable from "../CustomReportsStep/components/CustomReportStepUnavailable.vue";


export class AnalysisWizardNavigationService extends WizardNavigationService<AnalysisWizardSharedModel, AnalysisWizardStep, AnalysisWizardStepType> {

    static readonly injectableKey = 'AnalysisWizardNavigationService';

    get injectableKey() {
        return (this.constructor as typeof AnalysisWizardNavigationService).injectableKey;
    }

    private _stepOrder = Object.values(AnalysisWizardStepType) as (string | number)[];

    public getIsStepAvailable(stepType: AnalysisWizardStepType,
        sharedModel: AnalysisWizardSharedModel): WizardStepAvailabiltyModel {

        const model = new WizardStepAvailabiltyModel(false, undefined, stepType !== AnalysisWizardStepType.InputData);

        const hasSelectedInput = sharedModel?.inputFile != null;
        const hasCompletedNCAAnalysis = sharedModel?.analysisExecution?.id != null && sharedModel?.analysisExecution?.status === ExecutionStatusType.Succeeded;
        const hasNoCleanedFileOrConfiguredRules = sharedModel.cleanedFile == null && !sharedModel?.rules?.length;
        const hasCleanedFileAndConfiguredRules = sharedModel.cleanedFile != null && sharedModel?.rules?.length;

        const hasCompletedCustomCalculationExecution = sharedModel?.customCalculationsExecution?.id != null && sharedModel?.customCalculationsExecution?.status === ExecutionStatusType.Succeeded;
        const hasNoCalculationExecutionOrConfiguredCalculations = sharedModel.customCalculationsExecution == null && !sharedModel?.customCalculations?.length;

        const hasCompletedPostProcessingExecution = sharedModel?.postProcessingExecution?.id != null && sharedModel?.postProcessingExecution?.status === ExecutionStatusType.Succeeded;

        switch (stepType) {
            case AnalysisWizardStepType.InputData:
                model.isAvailable = true;
                break;
            case AnalysisWizardStepType.DataProcessing:
                model.unavailableComponent = DataProcessingStepUnavailable;
                if (hasSelectedInput) {
                    model.isAvailable = true;
                }
                break;
            case AnalysisWizardStepType.AnalysisConfiguration:
                model.unavailableComponent = AnalysisConfigurationStepUnavailable;
                if (hasSelectedInput && (hasNoCleanedFileOrConfiguredRules || hasCleanedFileAndConfiguredRules)) {
                    model.isAvailable = true;
                }
                break;
            case AnalysisWizardStepType.CustomCalculations:
                model.unavailableComponent = CustomCalculationStepUnavailable;
                if (hasSelectedInput && hasCompletedNCAAnalysis) {
                    model.isAvailable = true;
                }
                break;
            case AnalysisWizardStepType.PostProcessing:
                model.unavailableComponent = PostProcessingStepUnavailable;
                if (hasSelectedInput && hasCompletedNCAAnalysis && (hasNoCalculationExecutionOrConfiguredCalculations || hasCompletedCustomCalculationExecution)) {
                    model.isAvailable = true;
                }
                break;
            case AnalysisWizardStepType.Reports:
                model.unavailableComponent = CustomReportStepUnavailable;
                if (hasSelectedInput && hasCompletedNCAAnalysis && (hasCompletedPostProcessingExecution)) {
                    model.isAvailable = true;
                }
                break;
        }


        return model;

    }

    public getPreviousWizardStep(
        activeStep: AnalysisWizardStep,
        steps: AnalysisWizardStep[]): AnalysisWizardStep | undefined {

        if (activeStep == null || !steps?.length) return;

        const currentType = activeStep.type as string;
        if (!currentType) return undefined;

        const idx = this._stepOrder.indexOf(currentType);
        if (idx <= 0) return undefined;

        const prevType = this._stepOrder[idx - 1];
        return steps.find((s) => s.type === prevType);

    }

    public getNextWizardStep(
        activeStep: AnalysisWizardStep,
        steps: AnalysisWizardStep[]): AnalysisWizardStep | undefined {

        if (activeStep == null || !steps?.length) return;

        const currentType = activeStep?.type
        if (!currentType) return undefined

        const idx = this._stepOrder.indexOf(currentType)
        if (idx < 0 || idx >= this._stepOrder.length - 1) return undefined

        const nextType = this._stepOrder[idx + 1]
        return steps?.find((s) => s.type === nextType)

    }

    public setActiveWizardStep(step: AnalysisWizardStep, wizardModel: WizardModel<AnalysisWizardStep, AnalysisWizardSharedModel>): void {

        if (wizardModel == null) throw Error("wizardModel is required");
        if (step != null) {
            wizardModel.activeStep = step;
        }

    }

    public setActiveWizardStepByType(stepType: AnalysisWizardStepType, wizardModel: WizardModel<AnalysisWizardStep, AnalysisWizardSharedModel>): void {

        if (stepType == null) throw Error("stepType is required");
        if (wizardModel == null) throw Error("wizardModel is required");

        const step = wizardModel.steps?.find((s) => s.type === stepType)
        if (step != null) wizardModel.activeStep = step;

    }

    public trySetWizardActiveStepStatus(wizardModel: WizardModel<AnalysisWizardStep, AnalysisWizardSharedModel>, statusType: WizardStepStatusType, message?: string): void {

        if (wizardModel == null) throw Error("wizardModel is required");
        if (statusType == null) throw Error("statusType is required");
        const activeStep = wizardModel.activeStep;
        if (activeStep) {
            activeStep.statusType = statusType;
            activeStep.statusMessage = message;
        }

    }
}